import {toast} from '@wandb/weave/common/components/elements/Toast';
import {isEqual} from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useHistory} from 'react-router-dom';

import {useWeaveflowCurrentRouteContext} from '../../context';
import {TraceObjSchemaForBaseObjectClass} from '../wfReactInterface/objectClassQuery';
import {
  getDefaultComparisonViewLabel,
  getNewComparisonViewId,
  metricsDictToList,
  metricsListToDict,
  urlParamsToComparisonViewDefinition,
  useComparisonViewInstances,
  useCreateComparisonView,
} from './comparisonViewUtil';

type ComparisonViewContextType = {
  // Current view state
  viewId: string | null;
  baseView: TraceObjSchemaForBaseObjectClass<'ComparisonView'> | null;
  views: Array<TraceObjSchemaForBaseObjectClass<'ComparisonView'>>;
  isModified: boolean;
  isSaving: boolean;
  viewsLoading: boolean;

  // Actions
  onLoadView: (
    view: TraceObjSchemaForBaseObjectClass<'ComparisonView'>
  ) => void;
  onSaveView: () => void;
  onSaveNewView: () => void;
  onSaveTitle: (newTitle: string) => void;
  onResetView: () => void;
  onDeleteView: () => void;
  refetchViews: () => Promise<void>;
};

const ComparisonViewContext = createContext<ComparisonViewContextType | null>(
  null
);

export const useComparisonView = () => {
  const ctx = useContext(ComparisonViewContext);
  if (ctx === null) {
    throw new Error(
      'useComparisonView must be used within ComparisonViewProvider'
    );
  }
  return ctx;
};

type ComparisonViewProviderProps = {
  entity: string;
  project: string;
  viewId: string | null;
  evaluationCallIds: string[];
  selectedMetrics: Record<string, boolean> | null;
  scoreFilters: any[];
  activeTab: 'summary' | 'results';
  children: React.ReactNode;
};

export const ComparisonViewProvider: React.FC<ComparisonViewProviderProps> = ({
  entity,
  project,
  viewId,
  evaluationCallIds,
  selectedMetrics,
  scoreFilters,
  activeTab,
  children,
}) => {
  const history = useHistory();
  const routerContext = useWeaveflowCurrentRouteContext();
  const projectId = `${entity}/${project}`;

  const {
    views,
    loading: viewsLoading,
    refetchViews,
  } = useComparisonViewInstances(projectId);

  const createComparisonView = useCreateComparisonView(entity, project);
  const [isSaving, setIsSaving] = useState(false);

  const baseView = useMemo(() => {
    if (!viewId || viewsLoading) {
      return null;
    }
    return (
      (views.find(v => v.object_id === viewId) as
        | TraceObjSchemaForBaseObjectClass<'ComparisonView'>
        | undefined) ?? null
    );
  }, [viewId, views, viewsLoading]);

  const isModified = useMemo(() => {
    if (!baseView) return false;
    const viewDef = baseView.val.definition;

    const evalIdsChanged = !isEqual(
      evaluationCallIds,
      viewDef.evaluation_call_ids
    );

    const newMetricsList = metricsDictToList(selectedMetrics);
    const metricsChanged = !isEqual(
      newMetricsList,
      viewDef.selected_metrics ?? null
    );

    return evalIdsChanged || metricsChanged;
  }, [baseView, evaluationCallIds, selectedMetrics]);

  const onLoadView = useCallback(
    (view: TraceObjSchemaForBaseObjectClass<'ComparisonView'>) => {
      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          view.val.definition.evaluation_call_ids,
          metricsListToDict(view.val.definition.selected_metrics),
          [],
          'summary',
          view.object_id
        )
      );
    },
    [history, routerContext, entity, project]
  );

  const onSaveView = useCallback(async () => {
    if (!baseView) return;

    setIsSaving(true);
    try {
      const definition = urlParamsToComparisonViewDefinition({
        evaluationCallIds,
        metrics: selectedMetrics,
      });

      await createComparisonView(
        baseView.object_id,
        baseView.val.label,
        definition
      );

      await refetchViews();
    } catch (error) {
      toast(`Failed to save comparison: ${error}`, {type: 'error'});
    } finally {
      setIsSaving(false);
    }
  }, [
    baseView,
    evaluationCallIds,
    selectedMetrics,
    createComparisonView,
    refetchViews,
  ]);

  const onSaveNewView = useCallback(async () => {
    setIsSaving(true);
    try {
      const definition = urlParamsToComparisonViewDefinition({
        evaluationCallIds,
        metrics: selectedMetrics,
      });

      const newViewId = getNewComparisonViewId();
      const viewName = getDefaultComparisonViewLabel();
      await createComparisonView(newViewId, viewName, definition);
      await refetchViews();

      history.push(
        routerContext.compareEvaluationsUri(
          entity,
          project,
          evaluationCallIds,
          selectedMetrics,
          scoreFilters,
          activeTab,
          newViewId
        )
      );
    } catch (error) {
      toast(`Failed to create new comparison: ${error}`, {type: 'error'});
    } finally {
      setIsSaving(false);
    }
  }, [
    evaluationCallIds,
    selectedMetrics,
    scoreFilters,
    activeTab,
    createComparisonView,
    refetchViews,
    history,
    routerContext,
    entity,
    project,
  ]);

  const onSaveTitle = useCallback(
    async (newTitle: string) => {
      setIsSaving(true);
      try {
        const definition = urlParamsToComparisonViewDefinition({
          evaluationCallIds,
          metrics: selectedMetrics,
        });

        if (baseView) {
          await createComparisonView(baseView.object_id, newTitle, definition);
          await refetchViews();
        } else {
          const newViewId = getNewComparisonViewId();
          await createComparisonView(newViewId, newTitle, definition);
          await refetchViews();

          history.push(
            routerContext.compareEvaluationsUri(
              entity,
              project,
              evaluationCallIds,
              selectedMetrics,
              scoreFilters,
              activeTab,
              newViewId
            )
          );
        }
      } catch (error) {
        toast(`Failed to save comparison title: ${error}`, {type: 'error'});
      } finally {
        setIsSaving(false);
      }
    },
    [
      evaluationCallIds,
      selectedMetrics,
      scoreFilters,
      activeTab,
      baseView,
      createComparisonView,
      refetchViews,
      history,
      routerContext,
      entity,
      project,
    ]
  );

  const onResetView = useCallback(() => {
    if (!baseView) return;

    history.push(
      routerContext.compareEvaluationsUri(
        entity,
        project,
        baseView.val.definition.evaluation_call_ids,
        metricsListToDict(baseView.val.definition.selected_metrics),
        [],
        'summary',
        baseView.object_id
      )
    );
  }, [baseView, history, routerContext, entity, project]);

  const onDeleteView = useCallback(async () => {
    if (!baseView) return;

    history.push(
      routerContext.compareEvaluationsUri(
        entity,
        project,
        evaluationCallIds,
        selectedMetrics,
        scoreFilters,
        activeTab
      )
    );

    await refetchViews();
  }, [
    baseView,
    evaluationCallIds,
    selectedMetrics,
    scoreFilters,
    activeTab,
    refetchViews,
    history,
    routerContext,
    entity,
    project,
  ]);

  const value = useMemo(
    () => ({
      viewId,
      baseView,
      views,
      isModified,
      isSaving,
      viewsLoading,
      onLoadView,
      onSaveView,
      onSaveNewView,
      onSaveTitle,
      onResetView,
      onDeleteView,
      refetchViews,
    }),
    [
      viewId,
      baseView,
      views,
      isModified,
      isSaving,
      viewsLoading,
      onLoadView,
      onSaveView,
      onSaveNewView,
      onSaveTitle,
      onResetView,
      onDeleteView,
      refetchViews,
    ]
  );

  return (
    <ComparisonViewContext.Provider value={value}>
      {children}
    </ComparisonViewContext.Provider>
  );
};
